<template>
  <v-card id="tract-visit-form">
    <v-card-title class="primary">
      <span class="headline white--text">{{ title }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh hideFullscreen/>
    </v-card-title>
    <v-card-text>
      <v-row v-if="loading" class="my-12" justify="center" align="center">
        <v-progress-circular indeterminate color="primary"/>
      </v-row>
      <FormWrapper
      v-else
      formRef="tractVisitForm"
      buttonId="tract-visit-submit"
      :buttonText="buttonText"
      :disabled="!canSubmit || saving"
      @submit="submitClicked">
        <v-container fluid class="pt-4">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-autocomplete
              ref="visitType"
              color="black"
              item-color="primary"
              v-model="visit.tractVisitTypeId"
              :items="allTractVisitTypes"
              :rules="[rules.requiredNum]"
              itemText="name"
              itemValue="tractVisitTypeId"
              :label="$t('visitType')"
              id="tract-visit-type-autocomplete">
                <template #item="{ item }">
                  <v-row no-gutters>
                    <v-col>
                      {{ item.name }}
                      <div v-if="isMobile && !!item.description" class="caption grey--text text--darken-1">
                        {{ item.description }}
                      </div>
                    </v-col>
                    <v-col cols="auto" v-if="!isMobile">
                      <Icon v-if="!!item.description"
                      :small="false"
                      iconColor="info"
                      icon="mdi-information-outline"
                      :tooltipText="item.description"
                      margin="mx-1"
                      />
                    </v-col>
                  </v-row>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="6" sm="3">
              <CompactDatePicker
              :label="$t('completedAt')"
              :initialDate="completedInitial"
              :rules="[rules.required]"
              :clearable="false"
              @date-picked="completedDate = $event"/>
            </v-col>
            <v-col cols="6" sm="3">
              <TimePicker
              :label="$t('time')"
              :startTime="completedTime"
              :expandable="false"
              isEditing
              @time-picked="completedTime = $event"
              @valid-time="isValidCompletedTime = $event"/>
            </v-col>
            <v-col cols="12">
              <v-textarea
              color="black"
              outlined
              v-model="visit.notes"
              :label="$t('notes')"
              rows="3"
              auto-grow
              :counter="maxNotesLength"
              :maxlength="maxNotesLength"/>
            </v-col>
            <v-col cols="12" class="mt-4">
              <TractVisitAttachments
              :tract="tract"
              :files="tableFiles"
              :loading="loadingFiles"
              :label="$t('attachments')"
              :allowDelete="isVisitCreator"
              @file-chosen="queueFile"
              @delete="deleteExistingFileClicked"
              @remove="removePending"/>
            </v-col>
          </v-row>
        </v-container>
      </FormWrapper>
    </v-card-text>

    <v-dialog v-model="deleteFileConfirmation" width="400px">
      <ConfirmDialog
      v-if="deleteFileConfirmation"
      :title="$t('deleteVisitFile')"
      :body="$t('deleteVisitFileBody', { filename: fileForDelete.name })"
      :confirmText="$t('deleteFile')"
      @confirm="deleteExistingFile"
      @cancel="deleteFileConfirmation = false"/>
    </v-dialog>
  </v-card>
</template>

<script>
import moment from 'moment'
import { cloneDeep } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import fieldRules from '@/utils/rules.js'
import { StringConstants } from '@/utils/constants'
import { localToUTC } from '@/utils/DateFormatter.js'
import { MOBILE_BREAKPOINT } from '@/utils/DeviceUtility'
import { tractVisitFilesClient } from '@/utils/TractVisitFiles'
import { uniqueFileName } from '@/utils/Files'

export default {
  name: 'TractVisitForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    CompactDatePicker: () => import('@/components/helper/CompactDatePicker.vue'),
    TimePicker: () => import('@/components/helper/TimePicker.vue'),
    TractVisitAttachments: () => import('@/components/tract/tract-visits/TractVisitAttachments.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    },
    propVisit: {
      type: Object,
      required: false,
      default: undefined
    }
  },

  data: () => ({
    visit: {
      tractVisitTypeId: null,
      notes: ''
    },
    completedInitial: '',
    completedDate: '',
    completedTime: '',
    isValidCompletedTime: true,
    existingFiles: [],
    pendingFiles: [],
    loading: false,
    loadingFiles: false,
    saving: false,
    deleteFileConfirmation: false,
    fileForDelete: undefined,
    maxNotesLength: StringConstants.SuperLongStringLength,
    rules: fieldRules.rules
  }),

  computed: {
    ...mapGetters('tract-visit-type', ['allTractVisitTypes']),
    ...mapGetters('user', ['userInfo']),

    editing () {
      return !!this.visit?.tractVisitId
    },

    isVisitCreator () {
      return !this.editing || this.visit.createdByUserId === this.userInfo.applicationUserId
    },

    title () {
      return this.editing ? this.$t('editVisit') : this.$t('logVisit')
    },

    buttonText () {
      return this.editing ? this.$t('saveChanges') : this.$t('create')
    },

    isMobile () {
      return this.$vuetify.breakpoint.width < MOBILE_BREAKPOINT
    },

    tableFiles () {
      const pending = this.pendingFiles.map(file => ({
        name: file.name,
        type: file.name.split('.').pop(),
        contentType: file.type,
        isImage: file.type.startsWith('image/'),
        pending: true,
        file
      }))
      return [...this.existingFiles, ...pending]
    },

    canSubmit () {
      return this.visit.tractVisitTypeId !== null &&
        !!this.completedDate &&
        this.isValidCompletedTime
    }
  },

  async created () {
    if (this.propVisit) {
      this.visit = cloneDeep(this.propVisit)
      this.visit.notes = this.visit.notes ?? ''
      this.seedCompleted(this.visit.completedAt)
      this.loading = true
      try {
        await this.loadExistingFiles()
      } finally {
        this.loading = false
      }
    } else {
      this.seedCompleted(moment.utc().format())
    }
    this.focusVisitType()
  },

  methods: {
    ...mapActions('tract-visit', ['createTractVisit', 'updateTractVisit']),

    focusVisitType () {
      if (this.isMobile) return
      setTimeout(() => this.$refs.visitType?.focus(), 0)
    },

    seedCompleted (utcDate) {
      const local = moment.utc(utcDate).local()
      this.completedInitial = utcDate
      this.completedDate = local.format()
      this.completedTime = `${local.hours()}:${local.minutes()}`
    },

    toUtc (date, time) {
      const [hours, minutes] = time.split(':')
      return localToUTC(moment(date).hours(hours).minutes(minutes).format(), true)
    },

    async loadExistingFiles () {
      this.loadingFiles = true
      try {
        this.existingFiles = await tractVisitFilesClient.getFiles(this.visit.tractVisitId)
      } finally {
        this.loadingFiles = false
      }
    },

    queueFile (file) {
      const takenNames = this.tableFiles.map(f => f.name)
      const name = uniqueFileName(file.name, takenNames)
      this.pendingFiles.push(name === file.name ? file : new File([file], name, { type: file.type }))
    },

    removePending (item) {
      this.pendingFiles = this.pendingFiles.filter(f => f !== item.file)
    },

    deleteExistingFileClicked (file) {
      this.fileForDelete = file
      this.deleteFileConfirmation = true
    },

    async deleteExistingFile () {
      this.deleteFileConfirmation = false
      this.loadingFiles = true
      await tractVisitFilesClient.deleteFile(this.tract.tractId, this.fileForDelete.ref)
      await this.loadExistingFiles()
    },

    async submitClicked () {
      if (!this.canSubmit) return

      const requestObj = {
        tractId: this.tract.tractId,
        tractVisitTypeId: this.visit.tractVisitTypeId,
        completedAt: this.toUtc(this.completedDate, this.completedTime),
        notes: (this.visit.notes ?? '').trim() || null
      }
      if (this.editing) requestObj.tractVisitId = this.visit.tractVisitId

      this.saving = true
      try {
        const saved = this.editing
          ? await this.updateTractVisit({ tractId: this.tract.tractId, visit: requestObj })
          : await this.createTractVisit(requestObj)

        for (const file of this.pendingFiles) {
          await tractVisitFilesClient.uploadFile(saved, file)
        }

        this.setSnack(this.$t(this.editing ? 'visitUpdated' : 'visitLogged'))
        this.$emit('visit-mutated', saved)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
